/**
 * Calendar-date math for "time from today until a picked date" formulas.
 *
 * Kept separate from weightCalc.ts (which is weight/goal-pace domain) because
 * these are generic date helpers. All comparisons are done on LOCAL calendar
 * days — "today" is the visitor's own local date, matching the DatePicker's
 * `preventPastDates` lower bound — and are DST-safe (the day count is computed
 * from UTC-normalized midnights so a clock shift never adds/drops a day).
 */

/** Normalize a `YYYY-MM-DD` string or a Date to local midnight. Null if invalid. */
function toLocalMidnight(value: string | Date): Date | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  if (typeof value !== 'string' || !value) return null
  const parsed = new Date(`${value.slice(0, 10)}T00:00:00`)
  if (Number.isNaN(parsed.getTime())) return null
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate())
}

/**
 * Whole calendar days from `now` (default: the visitor's local "today") until
 * `target`. Positive for future dates, negative for past ones, 0 for the same
 * day. Returns 0 when either input is invalid.
 */
export function getDaysUntil(target: string | Date, now: Date = new Date()): number {
  const targetMidnight = toLocalMidnight(target)
  const fromMidnight = toLocalMidnight(now)
  if (!targetMidnight || !fromMidnight) return 0
  const fromUtc = Date.UTC(
    fromMidnight.getFullYear(),
    fromMidnight.getMonth(),
    fromMidnight.getDate(),
  )
  const targetUtc = Date.UTC(
    targetMidnight.getFullYear(),
    targetMidnight.getMonth(),
    targetMidnight.getDate(),
  )
  return Math.round((targetUtc - fromUtc) / 86400000)
}

/**
 * `YYYY-MM-DD` for a Date, read off the LOCAL calendar — matching the local-day
 * semantics of `getDaysUntil` above (`toISOString().slice(0, 10)` reads the UTC
 * calendar instead, so it reports the previous/next day for a visitor whose
 * local date differs from UTC at that moment).
 *
 * Returns null for an invalid / out-of-range Date instead of throwing the way
 * `toISOString()` does ("RangeError: Invalid time value"), so callers can skip
 * the write rather than take down the surrounding watcher or computed.
 */
export function formatLocalIsoDate(value: Date): string | null {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null
  const year = String(value.getFullYear()).padStart(4, '0')
  const month = String(value.getMonth() + 1).padStart(2, '0')
  const day = String(value.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** Day-count → week/month rounding terms a FITNESS_FUNCTIONS config may override. */
export interface PeriodRoundingParams {
  /** Spare days past a whole week at which the week count rounds up (7 = never). */
  weekRoundUpFromDays: number
  /** Length of a month, days. */
  daysPerMonth: number
  /** Spare days past a whole month at which the month count rounds up. */
  monthRoundUpFromDays: number
}

export const DEFAULT_PERIOD_ROUNDING_PARAMS: Readonly<PeriodRoundingParams> = {
  weekRoundUpFromDays: 4,
  daysPerMonth: 30,
  monthRoundUpFromDays: 14,
}

/**
 * Whole weeks from a day count. Rounds up once the spare days past a whole
 * week reach 4 (product rule): 17 days → 2 weeks (3 spare), 18 days → 3 weeks
 * (4 spare). Negative/zero day counts yield 0.
 */
export function getWeeksFromDays(
  days: number,
  { weekRoundUpFromDays }: PeriodRoundingParams = DEFAULT_PERIOD_ROUNDING_PARAMS,
): number {
  const totalDays = Number(days)
  if (!Number.isFinite(totalDays) || totalDays <= 0) return 0
  const wholeWeeks = Math.floor(totalDays / 7)
  return totalDays % 7 >= weekRoundUpFromDays ? wholeWeeks + 1 : wholeWeeks
}

/**
 * Whole months from a day count (30-day months). Rounds up once the spare days
 * past a whole month reach 14 (product rule): 43 days → 1 month (13 spare),
 * 44 days → 2 months (14 spare). Negative/zero day counts yield 0.
 */
export function getMonthsFromDays(
  days: number,
  { daysPerMonth, monthRoundUpFromDays }: PeriodRoundingParams = DEFAULT_PERIOD_ROUNDING_PARAMS,
): number {
  const totalDays = Number(days)
  if (!Number.isFinite(totalDays) || totalDays <= 0) return 0
  const wholeMonths = Math.floor(totalDays / daysPerMonth)
  return totalDays % daysPerMonth >= monthRoundUpFromDays ? wholeMonths + 1 : wholeMonths
}
